import type { TemplateProps } from '@/types/template';
import { cvLabel, languageLevelLabel, skillLevelLabel } from '@/i18n/cvLabels';
import { formatDateRange } from '@/utils/date';
import { fullName, isSectionEmpty, orderedSections } from '@/templates/shared/utils';
import { RichTextView } from '@/components/common/RichTextView';

export function ClassicHtml({ data, customColor, photoUrl }: TemplateProps) {
  const lang = data.settings.language;
  const accent = customColor ?? data.settings.color ?? '#1f2937';
  const sections = orderedSections(data);
  const present = cvLabel(lang, 'present');

  return (
    <article
      className="mx-auto bg-white text-[10pt] leading-snug text-slate-900"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '18mm 16mm',
        fontFamily: 'Georgia, serif',
      }}
    >
      <header className="border-b pb-3" style={{ borderColor: accent }}>
        <div className="flex items-start gap-4">
          {photoUrl && (
            <img
              src={photoUrl}
              alt=""
              className="h-24 w-24 rounded-md object-cover"
              style={{ borderRadius: 4 }}
            />
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: accent }}>
              {fullName(data) || '—'}
            </h1>
            {data.personalInfo.title && (
              <p className="mt-0.5 text-sm uppercase tracking-wider text-slate-700">
                {data.personalInfo.title}
              </p>
            )}
            <ContactLine data={data} />
          </div>
        </div>
      </header>

      {data.personalInfo.summary && (
        <Section title={cvLabel(lang, 'summary')} accent={accent}>
          <RichTextView markdown={data.personalInfo.summary} />
        </Section>
      )}

      {sections.map((key) => {
        if (isSectionEmpty(data, key)) return null;
        switch (key) {
          case 'experience':
            return (
              <Section key={key} title={cvLabel(lang, 'experience')} accent={accent}>
                {data.experience.map((e) => (
                  <div key={e.id} className="mb-3">
                    <div className="flex items-baseline justify-between">
                      <p className="font-semibold">
                        {e.position}
                        {e.company && (
                          <span className="font-normal text-slate-700"> · {e.company}</span>
                        )}
                      </p>
                      <span className="text-xs text-slate-600">
                        {formatDateRange(e.startDate, e.endDate, {
                          presentLabel: present,
                          locale: lang,
                        })}
                      </span>
                    </div>
                    {e.location && <p className="text-xs italic text-slate-600">{e.location}</p>}
                    {e.description && (
                      <div className="mt-1">
                        <RichTextView markdown={e.description} />
                      </div>
                    )}
                    {e.achievements && e.achievements.length > 0 && (
                      <ul className="mt-1 list-disc pl-5">
                        {e.achievements.map((a, i) => (
                          <li key={i}>
                            <RichTextView
                              markdown={a}
                              paragraphClassName="m-0"
                              listClassName="list-[circle] pl-5"
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </Section>
            );
          case 'education':
            return (
              <Section key={key} title={cvLabel(lang, 'education')} accent={accent}>
                {data.education.map((e) => (
                  <div key={e.id} className="mb-3">
                    <div className="flex items-baseline justify-between">
                      <p className="font-semibold">
                        {e.degree}
                        {e.field && <span className="font-normal"> · {e.field}</span>}
                      </p>
                      <span className="text-xs text-slate-600">
                        {formatDateRange(e.startDate, e.endDate, {
                          presentLabel: present,
                          locale: lang,
                        })}
                      </span>
                    </div>
                    <p className="text-sm italic">{e.institution}</p>
                    {e.description && (
                      <div className="mt-1">
                        <RichTextView markdown={e.description} />
                      </div>
                    )}
                  </div>
                ))}
              </Section>
            );
          case 'skills':
            return (
              <Section key={key} title={cvLabel(lang, 'skills')} accent={accent}>
                <SkillGroups data={data} lang={lang} />
              </Section>
            );
          case 'languages':
            return (
              <Section key={key} title={cvLabel(lang, 'languages')} accent={accent}>
                <ul className="grid grid-cols-2 gap-1">
                  {data.languages.map((l) => (
                    <li key={l.id}>
                      <span className="font-medium">{l.name}</span> —{' '}
                      {languageLevelLabel(lang, l.level)}
                    </li>
                  ))}
                </ul>
              </Section>
            );
          case 'projects':
            return (
              <Section key={key} title={cvLabel(lang, 'projects')} accent={accent}>
                {data.projects?.map((p) => (
                  <div key={p.id} className="mb-2">
                    <p className="font-semibold">
                      {p.name}
                      {p.url && (
                        <span className="ml-2 text-xs font-normal text-slate-600">{p.url}</span>
                      )}
                    </p>
                    {p.technologies && p.technologies.length > 0 && (
                      <p className="text-xs italic">{p.technologies.join(', ')}</p>
                    )}
                    {p.description && <RichTextView markdown={p.description} />}
                  </div>
                ))}
              </Section>
            );
          case 'certifications':
            return (
              <Section key={key} title={cvLabel(lang, 'certifications')} accent={accent}>
                <ul>
                  {data.certifications?.map((c) => (
                    <li key={c.id} className="mb-1">
                      <span className="font-medium">{c.name}</span> — {c.issuer}
                      <span className="ml-2 text-xs text-slate-600">{c.date}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            );
          case 'customSections':
            return (
              <div key={key}>
                {data.customSections?.map((c) => (
                  <Section key={c.id} title={c.title} accent={accent}>
                    <RichTextView markdown={c.content} />
                  </Section>
                ))}
              </div>
            );
          default:
            return null;
        }
      })}
    </article>
  );
}

function Section({
  title,
  accent,
  children,
}: {
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-4">
      <h2
        className="mb-2 border-b pb-0.5 text-sm font-bold uppercase tracking-wider"
        style={{ color: accent, borderColor: accent }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function ContactLine({ data }: { data: TemplateProps['data'] }) {
  const { email, phone, address, websites } = data.personalInfo;
  const items: React.ReactNode[] = [];
  if (email) items.push(email);
  if (phone) items.push(phone);
  if (address?.city) items.push([address.city, address.country].filter(Boolean).join(', '));
  (websites ?? []).forEach((w) =>
    items.push(
      <span key={w.id}>
        {w.label}:{' '}
        <a href={w.url} className="underline-offset-2 hover:underline">
          {w.displayText || w.url}
        </a>
      </span>,
    ),
  );
  if (items.length === 0) return null;
  return (
    <p className="mt-1 text-xs text-slate-700">
      {items.map((item, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          {item}
        </span>
      ))}
    </p>
  );
}

function SkillGroups({
  data,
  lang,
}: {
  data: TemplateProps['data'];
  lang: TemplateProps['data']['settings']['language'];
}) {
  const groups = new Map<string, typeof data.skills>();
  data.skills.forEach((s) => {
    const key = s.category ?? '';
    const arr = groups.get(key) ?? [];
    arr.push(s);
    groups.set(key, arr);
  });
  return (
    <div className="space-y-1">
      {Array.from(groups.entries()).map(([cat, skills]) => (
        <div key={cat || 'no-cat'}>
          {cat && <span className="font-semibold">{cat}: </span>}
          <span>
            {skills
              .map((s) => (s.level ? `${s.name} (${skillLevelLabel(lang, s.level)})` : s.name))
              .join(', ')}
          </span>
        </div>
      ))}
    </div>
  );
}
